/**
 * maintenanceGuard.ts — decide QUANDO montare la schermata «RideConnect è in
 * manutenzione» (overlay globale ascoltato da App.tsx).
 *
 * Estratto da apiFetch.ts (30 Ott 2026) per poter verificare il comportamento
 * con i timer finti, senza simulare `fetch`.
 *
 * Regole (aggiornate il 30 Ott 2026 dopo la segnalazione di Giuseppe —
 * «clicco Notizie, mi dice che è in manutenzione, poi dopo qualche secondo
 * parte»):
 *  1. **una segnalazione per richiesta**, non una per tentativo di retry:
 *     prima un solo `503` seguito dal retry andato a buon fine montava la
 *     schermata, perché i «due colpi» erano i due tentativi della STESSA
 *     chiamata;
 *  2. servono `MAINTENANCE_HITS_NEEDED` segnalazioni entro
 *     `MAINTENANCE_WINDOW_MS` (richieste distinte, tipico di un backend
 *     davvero giù: elenco + status + sezioni falliscono insieme);
 *  3. l'overlay si monta solo dopo `MAINTENANCE_GRACE_MS` di grazia: copre
 *     l'intero budget dei retry di apiFetch (immediato + 1 s + 2 s + 4 s) e il
 *     cooldown di 15 s del circuit breaker del DB, quindi né un cold start
 *     Render/Neon né un breaker appena aperto fanno lampeggiare una schermata
 *     a tutto schermo;
 *  4. qualsiasi `2xx` (`clearMaintenanceState`) annulla l'attesa in corso e
 *     azzera il contatore; lo stesso vale per il clear esplicito della
 *     schermata (pulsante «Riprova ora» / probe automatica), che dispatcha
 *     l'evento senza passare da apiFetch.
 */

export const MAINTENANCE_EVENT = "rideconnect:maintenance";
export const MAINTENANCE_CLEAR_EVENT = "rideconnect:maintenance-clear";

/** Finestra entro cui due segnalazioni contano come «servizio giù». */
export const MAINTENANCE_WINDOW_MS = 15_000;
/**
 * Quanto aspettare prima di coprire l'app.
 *
 * 8 s ≈ budget completo dei retry di apiFetch (immediato + 1 s + 2 s + 4 s):
 * copre sia il cold start Render/Neon sia il cooldown del circuit breaker del
 * DB (15 s, `CB_COOLDOWN_MS` in `server/src/db/pool.ts`), che è la causa più
 * frequente del «manutenzione» lampo sul pannello admin. Un'onda più lunga
 * (guasto vero) mostra comunque l'overlay, con 8 s di ritardo al massimo.
 */
export const MAINTENANCE_GRACE_MS = 8_000;
/** Segnalazioni necessarie (richieste distinte) per mostrare l'overlay. */
export const MAINTENANCE_HITS_NEEDED = 2;

const HITS_KEY = "__rcMaintenanceHits";
const GRACE_KEY = "__rcMaintenanceGrace";

type GuardWindow = Record<string, unknown>;

function guardWindow(): GuardWindow | null {
  return typeof window === "undefined"
    ? null
    : (window as unknown as GuardWindow);
}

/** Annulla l'overlay in attesa (arrivato un 2xx o un clear esplicito). */
function cancelPending(): void {
  const w = guardWindow();
  const pending = w?.[GRACE_KEY];
  if (w && pending) {
    clearTimeout(pending as ReturnType<typeof setTimeout>);
    w[GRACE_KEY] = null;
  }
}

function resetHits(): void {
  const w = guardWindow();
  if (w) w[HITS_KEY] = { count: 0, at: 0 };
}

/**
 * Registra il fallimento di UNA richiesta (503 a retry esauriti).
 * Va chiamata una sola volta per richiesta: mai dentro il loop dei retry.
 */
export function signalMaintenanceFailure(): void {
  const w = guardWindow();
  if (!w) return;

  const now = Date.now();
  const prev = (w[HITS_KEY] as { count: number; at: number }) || {
    count: 0,
    at: 0,
  };
  const count = now - prev.at < MAINTENANCE_WINDOW_MS ? prev.count + 1 : 1;
  w[HITS_KEY] = { count, at: now };

  if (count < MAINTENANCE_HITS_NEEDED) return;
  // Overlay già programmato: non riprogrammare (né allungare l'attesa).
  if (w[GRACE_KEY]) return;

  w[GRACE_KEY] = setTimeout(() => {
    w[GRACE_KEY] = null;
    if (typeof window !== "undefined") {
      window.dispatchEvent(new CustomEvent(MAINTENANCE_EVENT));
    }
  }, MAINTENANCE_GRACE_MS);
}

/** Segnale di servizio disponibile: azzera contatore e attesa in corso. */
export function clearMaintenanceState(): void {
  if (typeof window === "undefined") return;
  cancelPending();
  resetHits();
  window.dispatchEvent(new CustomEvent(MAINTENANCE_CLEAR_EVENT));
}

// Il pulsante «Riprova ora» e la probe automatica di MaintenanceScreen
// dispatchano il clear direttamente: senza questo listener il contatore
// resterebbe a 2 e il primo 503 successivo rimonterebbe l'overlay.
if (typeof window !== "undefined") {
  window.addEventListener(MAINTENANCE_CLEAR_EVENT, () => {
    cancelPending();
    resetHits();
  });
}

/** Solo per i test: azzera lo stato senza dispatchare eventi. */
export function resetMaintenanceGuardForTests(): void {
  cancelPending();
  resetHits();
}
